import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Button,
  Form,
  InputGroup,
  Table,
  Spinner,
} from "@themesberg/react-bootstrap";
import {
  FiX,
  FiEye,
  FiEyeOff,
  FiPower,
} from "react-icons/fi";
import {
  userGetSubIdDetail,
  userGetSubIdNetwork,
  userChangeSubIdPassword,
  userToggleSubIdStatus,
} from "../../../api";
import BankDetailForm from "./BankDetailForm";
import { formatCurrency } from "./IdCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faLock } from "@fortawesome/free-solid-svg-icons";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "earnings", label: "Earnings" },
  { key: "network", label: "Network" },
  { key: "settings", label: "Settings" },
];

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

export default function IdDetailDrawer({
  show,
  item,
  initialTab = "overview",
  ownerCustomUserId,
  onClose,
  onUpdated,
}) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [detail, setDetail] = useState(null);
  const [network, setNetwork] = useState([]);
  const [loading, setLoading] = useState(false);
  const [networkLoading, setNetworkLoading] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    password: "",
    password_confirmation: "",
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);

   const [errors, setErrors] = useState({});

  useEffect(() => {
    if (show && item?.custom_user_id) {
      setActiveTab(initialTab);
      loadDetail(item.custom_user_id);
    }
  }, [show, item?.custom_user_id, initialTab]);

  useEffect(() => {
    if (show && activeTab === "network" && item?.custom_user_id) {
      loadNetwork(item.custom_user_id);
    }
  }, [show, activeTab, item?.custom_user_id]);

  const loadDetail = async (customUserId) => {
    try {
      setLoading(true);
      const response = await userGetSubIdDetail(customUserId);
      setDetail(response?.data?.data || null);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to load ID details");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const resetPasswordState = () => {
    setPasswordForm({
      password: "",
      password_confirmation: "",
    });
    setErrors({});
    setPasswordErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
  };
  
  useEffect(() => {
    if (!show) {
      resetPasswordState();
    }
  }, [show]);

  const loadNetwork = async (customUserId) => {
    try {
      setNetworkLoading(true);
      const response = await userGetSubIdNetwork(customUserId);
      const data = response?.data?.data || {};
      setNetwork(Array.isArray(data.network) ? data.network : []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to load network");
      setNetwork([]);
    } finally {
      setNetworkLoading(false);
    }
  };

  const validatePassword = () => {
    const errors = {};
    if (!passwordForm.password) errors.password = "Password is required.";
    else if (passwordForm.password.length < 6) {
      errors.password = "Password must be at least 6 characters.";
    }
    if (!passwordForm.password_confirmation) {
      errors.password_confirmation = "Please confirm the password.";
    } else if (passwordForm.password !== passwordForm.password_confirmation) {
      errors.password_confirmation = "Passwords do not match.";
    }
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!item?.custom_user_id || !validatePassword()) return;

    try {
      setChangingPassword(true);
      const response = await userChangeSubIdPassword(item.custom_user_id, {
        password: passwordForm.password,
        password_confirmation: passwordForm.password_confirmation,
      });
      toast.success(response?.data?.message || "Password updated successfully");
      setPasswordForm({ password: "", password_confirmation: "" });
      setShowPassword(false);
      setShowConfirmPassword(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update password");
    } finally {
      setChangingPassword(false);
    }
  };

    const handleChange = (e) => {
    const { name, value } = e.target;

    setPasswordForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => {
      const newErrors = { ...prev };

      // Clear error when typing
      delete newErrors[name];

      // LIVE confirm password validation
      if (name === "password_confirmation") {
        if (value && value !== passwordForm.password) {
          newErrors.password_confirmation = "Passwords do not match";
        } else {
          delete newErrors.password_confirmation;
        }
      }

      // If password changes, re-check confirm password
      if (name === "password" && passwordForm.password_confirmation) {
        if (passwordForm.password_confirmation !== value) {
          newErrors.password_confirmation = "Passwords do not match";
        } else {
          delete newErrors.password_confirmation;
        }
      }

      return newErrors;
    });
  };

  const handleToggleStatus = async () => {
    if (!item?.custom_user_id) return;
    const isActive = String(detail?.status || item.status).toLowerCase() === "active";
    const action = isActive ? "deactivate" : "activate";
    if (!window.confirm(`Are you sure you want to ${action} this ID?`)) return;

    try {
      setTogglingStatus(true);
      const response = await userToggleSubIdStatus(item.custom_user_id);
      toast.success(response?.data?.message || "Status updated");
      await loadDetail(item.custom_user_id);
      onUpdated?.();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update status");
    } finally {
      setTogglingStatus(false);
    }
  };

  if (!show || !item) return null;

  const status = detail?.status || item.status;
  const isBlocked = String(status).toLowerCase() === "blocked";
  const isActive = String(status).toLowerCase() === "active";

   const getPasswordRules = (password) => {
    const value = String(password || "");
    return {
      uppercase: /[A-Z]/.test(value),
      lowercase: /[a-z]/.test(value),
      number: /\d/.test(value),
      special: /[@$!%*?&]/.test(value),
      minLength: value.length >= 8,
    };
  };

  const isPasswordValid = (password) => {
    const rules = getPasswordRules(password);
    return Object.values(rules).every(Boolean);
  };

  const getConfirmPasswordError = (password, confirmPassword) => {
    const confirm = String(confirmPassword || "");
    if (!confirm) return "Confirm password is required.";
    if (password !== confirm) return "Passwords do not match.";
    return "";
  };


  const passwordRules = getPasswordRules(passwordForm.password);

  return (
    <>
      <div className="my-ids-drawer-overlay" onClick={onClose} role="presentation" />
      <div className="my-ids-drawer">
        <div className="my-ids-drawer-header">
          <div>
            <h5 className="mb-0 fw-bold">{item.name}</h5>
            <small className="text-muted">{item.custom_user_id}</small>
          </div>
          <Button variant="link" className="text-dark p-0" onClick={onClose}>
            <FiX size={22} />
          </Button>
        </div>

        <div className="my-ids-drawer-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`my-ids-drawer-tab ${activeTab === tab.key ? "active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="my-ids-drawer-body">
          {loading ? (
            <div className="text-center py-5 text-muted">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading details...
            </div>
          ) : (
            <>
              {activeTab === "overview" && detail && (
                <>
                  <Table borderless className="my-ids-detail-table mb-4">
                    <tbody>
                      <tr>
                        <th>Custom User ID</th>
                        <td>{detail.custom_user_id}</td>
                      </tr>
                      <tr>
                        <th>Username</th>
                        <td>{detail.username || "N/A"}</td>
                      </tr>
                      <tr>
                        <th>Name</th>
                        <td>{detail.name}</td>
                      </tr>
                      <tr>
                        <th>Referral Number</th>
                        <td>{detail.referral_number}</td>
                      </tr>
                      <tr>
                        <th>Status</th>
                        <td className="text-capitalize">{detail.status}</td>
                      </tr>
                      <tr>
                        <th>Placed Under</th>
                        <td>
                          {detail.placed_under?.name || "N/A"}
                          {detail.placed_under?.custom_user_id
                            ? ` (${detail.placed_under.custom_user_id})`
                            : ""}
                        </td>
                      </tr>
                      <tr>
                        <th>Level</th>
                        <td>{detail.level ?? "N/A"}</td>
                      </tr>
                      <tr>
                        <th>KYC Status</th>
                        <td className="text-capitalize">{detail.kyc_status || "N/A"}</td>
                      </tr>
                      <tr>
                        <th>Wallet Balance</th>
                        <td>{formatCurrency(detail.wallet_balance)}</td>
                      </tr>
                      <tr>
                        <th>Direct Referrals</th>
                        <td>{detail.direct_referrals ?? 0}</td>
                      </tr>
                      <tr>
                        <th>Network Size</th>
                        <td>{detail.network_size ?? 0}</td>
                      </tr>
                      <tr>
                        <th>Created</th>
                        <td>{formatDate(detail.created_at)}</td>
                      </tr>
                    </tbody>
                  </Table>

                  {Array.isArray(detail.direct_referrals_list) &&
                    detail.direct_referrals_list.length > 0 && (
                      <>
                        <h6 className="fw-bold mb-2">Direct Referrals</h6>
                        <div className="table-responsive">
                          <Table hover size="sm" className="align-middle">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>ID</th>
                                <th>Status</th>
                                <th>Joined</th>
                              </tr>
                            </thead>
                            <tbody>
                              {detail.direct_referrals_list.map((ref) => (
                                <tr key={ref.id}>
                                  <td>{ref.name}</td>
                                  <td>{ref.custom_user_id}</td>
                                  <td className="text-capitalize">{ref.status}</td>
                                  <td>{formatDate(ref.created_at)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </>
                    )}
                </>
              )}

              {activeTab === "earnings" && detail && (
                <>
                  <div className="my-ids-earnings-row mb-4">
                    <div>
                      <div className="my-ids-earnings-label">This Month</div>
                      <div className="my-ids-earnings-value">
                        {formatCurrency(detail.current_month_earnings)}
                      </div>
                    </div>
                    <div>
                      <div className="my-ids-earnings-label">All Time</div>
                      <div className="my-ids-earnings-value">
                        {formatCurrency(detail.all_time_earnings ?? detail.total_earnings)}
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">Wallet Balance</small>
                    <div className="fw-bold fs-5">{formatCurrency(detail.wallet_balance)}</div>
                  </div>

                  <h6 className="fw-bold mb-2">Commission Breakdown (This Month)</h6>
                  {Array.isArray(detail.monthly_commission_breakdown) &&
                  detail.monthly_commission_breakdown.length > 0 ? (
                    <div className="table-responsive">
                      <Table hover size="sm" className="align-middle">
                        <thead>
                          <tr>
                            <th>Level</th>
                            <th>Members</th>
                            <th className="text-end">Earned</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.monthly_commission_breakdown.map((row) => (
                            <tr key={row.level}>
                              <td>Level {Number(row.level ?? 0) + 1}</td>
                              <td>{row.members}</td>
                              <td className="text-end">{formatCurrency(row.earned)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-muted small">No commission earned this month.</p>
                  )}

                  {Array.isArray(detail.course_purchases) &&
                    detail.course_purchases.length > 0 && (
                      <>
                        <h6 className="fw-bold mb-2 mt-4">Course Purchases</h6>
                        <div className="table-responsive">
                          <Table hover size="sm" className="align-middle">
                            <thead>
                              <tr>
                                <th>Amount</th>
                                <th>Status</th>
                                <th>Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {detail.course_purchases.map((purchase) => (
                                <tr key={purchase.id}>
                                  <td>{formatCurrency(purchase.amount_paid)}</td>
                                  <td className="text-capitalize">{purchase.status}</td>
                                  <td>{formatDate(purchase.created_at)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </>
                    )}
                </>
              )}

              {activeTab === "network" && (
                <>
                  {networkLoading ? (
                    <div className="text-center py-4 text-muted">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Loading network...
                    </div>
                  ) : network.length > 0 ? (
                    <div className="my-ids-network-list">
                      {network.map((node, index) => (
                        <div key={`${node.id}-${index}`} className="my-ids-network-item">
                          <div className="fw-semibold">{node.name || "N/A"}</div>
                          <small className="text-muted">
                            Level {Number(node.level ?? 0) + 1}
                            {" · "}
                            Referred by: {node.referred_by_name || "-"}
                            {" · "}
                            Joined: {formatDate(node.created_at)}
                          </small>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted text-center py-4">No downline members yet.</p>
                  )}
                </>
              )}

              {activeTab === "settings" && detail && (
                <>
                  <h6 className="fw-bold mb-3">Change Password</h6>
                  {/* <Form onSubmit={handleChangePassword} className="mb-4 pb-4 border-bottom">
                    <Form.Group className="mb-3">
                      <Form.Label>New Password</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type={showPassword ? "text" : "password"}
                          value={passwordForm.password}
                          isInvalid={!!passwordErrors.password}
                          onChange={(e) =>
                            setPasswordForm((prev) => ({
                              ...prev,
                              password: e.target.value,
                            }))
                          }
                        />
                        <InputGroup.Text
                          role="button"
                          style={{ cursor: "pointer" }}
                          onClick={() => setShowPassword((prev) => !prev)}
                        >
                          {showPassword ? <FiEyeOff /> : <FiEye />}
                        </InputGroup.Text>
                        <Form.Control.Feedback type="invalid">
                          {passwordErrors.password}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Confirm Password</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type={showConfirmPassword ? "text" : "password"}
                          value={passwordForm.password_confirmation}
                          isInvalid={!!passwordErrors.password_confirmation}
                          onChange={(e) =>
                            setPasswordForm((prev) => ({
                              ...prev,
                              password_confirmation: e.target.value,
                            }))
                          }
                        />
                        <InputGroup.Text
                          role="button"
                          style={{ cursor: "pointer" }}
                          onClick={() => setShowConfirmPassword((prev) => !prev)}
                        >
                          {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                        </InputGroup.Text>
                        <Form.Control.Feedback type="invalid">
                          {passwordErrors.password_confirmation}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>

                    <Button
                      type="submit"
                      className="my-ids-btn-primary"
                      disabled={changingPassword}
                    >
                      {changingPassword ? "Updating..." : "Update Password"}
                    </Button>
                  </Form> */}

                  <Form onSubmit={handleChangePassword} className="mb-4 pb-4 border-bottom">
                    <Form.Group className="mb-3" controlId="password">
                      <Form.Label className="fw-semibold">
                        Password <span className="text-danger">*</span>
                      </Form.Label>
                      <InputGroup
                        className={`flex-nowrap align-items-stretch ${errors.password ? "invalid-input-group" : ""
                          }`}
                      >
                        <InputGroup.Text
                          className="bg-white"
                          style={{ minHeight: "46px" }}
                        >
                          <FontAwesomeIcon icon={faLock} />
                        </InputGroup.Text>

                        <Form.Control
                          name="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter Password"
                          value={passwordForm.password}
                          onChange={handleChange}
                          isInvalid={!!errors.password}
                          autoComplete="new-password"
                          className="py-2 border-start-0 border-end-0"
                          style={{
                            boxShadow: "none",
                            minHeight: "46px",
                          }}
                        />

                        <InputGroup.Text
                          className="p-0"
                          style={{
                            background: "#e9ecef",
                            minWidth: "58px",
                            justifyContent: "center",
                            borderColor: "#dee2e6",
                            minHeight: "46px",
                          }}
                        >
                          <Button
                            type="button"
                            variant="link"
                            onClick={() => setShowPassword((prev) => !prev)}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            className="w-100 h-100 p-0 text-decoration-none border-0 shadow-none d-flex align-items-center justify-content-center"
                            style={{
                              background: "transparent",
                              color: "#5f6b7a",
                              minHeight: "46px",
                            }}
                          >
                            <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                          </Button>
                        </InputGroup.Text>
                      </InputGroup>
                      <Form.Control.Feedback type="invalid" className="d-block">
                        {errors.password}
                      </Form.Control.Feedback>

                      {passwordForm.password && (
                        <ul className="mt-2 mb-0 ps-4" style={{ color: "#dc3545" }}>
                          {!passwordRules.uppercase && <li>At least 1 uppercase letter</li>}
                          {!passwordRules.lowercase && <li>At least 1 lowercase letter</li>}
                          {!passwordRules.number && <li>At least 1 number</li>}
                          {!passwordRules.special && <li>At least 1 special character (@$!%*?&)</li>}
                          {!passwordRules.minLength && <li>Minimum 8 characters</li>}
                        </ul>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="confirmPassword">
                      <Form.Label className="fw-semibold">
                        Confirm Password <span className="text-danger">*</span>
                      </Form.Label>
                      <InputGroup
                        className={`flex-nowrap align-items-stretch ${errors.password_confirmation ? "invalid-input-group" : ""
                          }`}
                      >
                        <InputGroup.Text
                          className="bg-white"
                          style={{ minHeight: "46px" }}
                        >
                          <FontAwesomeIcon icon={faLock} />
                        </InputGroup.Text>

                        <Form.Control
                          name="password_confirmation"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Re-enter Password"
                          value={passwordForm.password_confirmation}
                          onChange={handleChange}
                          isInvalid={!!errors.password_confirmation}
                          autoComplete="new-password"
                          className="py-2 border-start-0 border-end-0"
                          style={{
                            boxShadow: "none",
                            minHeight: "46px",
                          }}
                        />

                        <InputGroup.Text
                          className="p-0"
                          style={{
                            background: "#e9ecef",
                            minWidth: "58px",
                            justifyContent: "center",
                            borderColor: "#dee2e6",
                            minHeight: "46px",
                          }}
                        >
                          <Button
                            type="button"
                            variant="link"
                            onClick={() => setShowConfirmPassword((prev) => !prev)}
                            aria-label={
                              showConfirmPassword ? "Hide password" : "Show password"
                            }
                            className="w-100 h-100 p-0 text-decoration-none border-0 shadow-none d-flex align-items-center justify-content-center"
                            style={{
                              background: "transparent",
                              color: "#5f6b7a",
                              minHeight: "46px",
                            }}
                          >
                            <FontAwesomeIcon
                              icon={showConfirmPassword ? faEyeSlash : faEye}
                            />
                          </Button>
                        </InputGroup.Text>
                      </InputGroup>
                      <Form.Control.Feedback type="invalid" className="d-block">
                        {errors.password_confirmation}
                      </Form.Control.Feedback>
                    </Form.Group>


                    <Button
                      type="submit"
                      className="my-ids-btn-primary"
                      disabled={changingPassword}
                    >
                      {changingPassword ? "Updating..." : "Update Password"}
                    </Button>
                  </Form>

                  <BankDetailForm
                    customUserId={item.custom_user_id}
                    ownerCustomUserId={ownerCustomUserId || detail?.bank_details?.owner_custom_user_id}
                    initialData={detail.bank_details}
                  />

                  {!isBlocked && (
                    <div className="mt-4 pt-4 border-top">
                      <Button
                        variant={isActive ? "outline-warning" : "outline-success"}
                        className="d-inline-flex align-items-center gap-1"
                        disabled={togglingStatus}
                        onClick={handleToggleStatus}
                      >
                        <FiPower />
                        {togglingStatus
                          ? "Updating..."
                          : isActive
                          ? "Deactivate ID"
                          : "Activate ID"}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
